import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ArrowRight, Leaf, Award, Heart } from 'lucide-react';
import axios from 'axios';

const Home = () => {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await axios.get('/api/products/');
        setProducts(response.data);
      } catch (error) {
        console.error('Error fetching products:', error);
      }
    };
    fetchProducts();
  }, []);

  return (
    <div>
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden" style={{ backgroundColor: '#f7f5f2' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1
              className="text-5xl md:text-7xl font-bold mb-6 leading-tight"
              style={{ color: '#4a2c1a', fontFamily: 'Georgia, serif' }}
            >
              Pure Maple Syrup from the Heart of
              <span style={{ color: '#FF8C19' }}> Upstate NY</span>
            </h1>
            <p className="text-xl md:text-2xl mb-8" style={{ color: '#736c64' }}>
              Handcrafted with tradition. Harvested with care. Delivered to your table.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/shop">
                <Button
                  size="lg"
                  className="text-white font-semibold px-8 py-6 text-lg hover:opacity-90 transition-all transform hover:scale-105"
                  style={{ backgroundColor: '#FF8C19' }}
                >
                  Shop Now <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/about">
                <Button
                  size="lg"
                  variant="outline"
                  className="font-semibold px-8 py-6 text-lg border-2 hover:bg-orange-50 transition-all"
                  style={{ borderColor: '#FF8C19', color: '#FF8C19' }}
                >
                  Our Story
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Decorative maple leaf */}
        <div className="absolute top-10 left-10 opacity-10">
          <Leaf className="h-32 w-32" style={{ color: '#FF8C19' }} />
        </div>
        <div className="absolute bottom-10 right-10 opacity-10">
          <Leaf className="h-40 w-40" style={{ color: '#FF8C19' }} />
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20" style={{ backgroundColor: 'white' }}>
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-8 rounded-lg transition-transform hover:scale-105" style={{ backgroundColor: '#f7f5f2' }}>
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ backgroundColor: '#FF8C19' }}>
                <Leaf className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-3" style={{ color: '#4a2c1a' }}>
                100% Pure
              </h3>
              <p style={{ color: '#736c64' }}>
                No additives, no preservatives. Just pure, natural maple syrup from our trees to your table.
              </p>
            </div>

            <div className="text-center p-8 rounded-lg transition-transform hover:scale-105" style={{ backgroundColor: '#f7f5f2' }}>
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ backgroundColor: '#FF8C19' }}>
                <Heart className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-3" style={{ color: '#4a2c1a' }}>
                Family Crafted
              </h3>
              <p style={{ color: '#736c64' }}>
                Made with love by our small family team. Every batch receives personal attention and care.
              </p>
            </div>

            <div className="text-center p-8 rounded-lg transition-transform hover:scale-105" style={{ backgroundColor: '#f7f5f2' }}>
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ backgroundColor: '#FF8C19' }}>
                <Award className="h-8 w-8 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-3" style={{ color: '#4a2c1a' }}>
                Grade A Quality
              </h3>
              <p style={{ color: '#736c64' }}>
                Premium Grade A maple syrup, harvested at peak season for the best flavor and quality.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <section className="py-20" style={{ backgroundColor: '#f7f5f2' }}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold mb-4" style={{ color: '#4a2c1a', fontFamily: 'Georgia, serif' }}>
              Our Products
            </h2>
            <p className="text-xl" style={{ color: '#736c64' }}>
              Choose the perfect size for your needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {products.map((product) => (
              <div
                key={product.id}
                className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
              >
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-64 object-cover"
                />
                <div className="p-6">
                  <h3 className="text-2xl font-bold mb-2" style={{ color: '#4a2c1a' }}>
                    {product.name}
                  </h3>
                  <p className="mb-4" style={{ color: '#736c64' }}>
                    {product.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-3xl font-bold" style={{ color: '#FF8C19' }}>
                      ${product.price}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/shop">
              <Button
                size="lg"
                className="text-white font-semibold px-8 py-6 text-lg hover:opacity-90 transition-all"
                style={{ backgroundColor: '#FF8C19' }}
              >
                View All Products <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20" style={{ backgroundColor: '#4a2c1a' }}>
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#FF8C19', fontFamily: 'Georgia, serif' }}>
            Ready to taste the difference?
          </h2>
          <p className="text-xl mb-8" style={{ color: '#f7f5f2' }}>
            Order now and experience the pure, rich flavor of authentic Upstate New York maple syrup.
          </p>
          <Link to="/shop">
            <Button
              size="lg"
              className="text-white font-semibold px-8 py-6 text-lg hover:opacity-90 transition-all transform hover:scale-105"
              style={{ backgroundColor: '#FF8C19' }}
            >
              Start Shopping
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
